import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  PCFSoftShadowMap,
  SRGBColorSpace,
} from 'three';
import { CAMERA } from '../config/settings';

/**
 * Owns the renderer, camera and the two scenes (world + first-person overlay)
 * and knows how to draw a frame. It has no game logic — purely the rendering
 * substrate everything else draws into.
 */
export class Engine {
  readonly renderer: WebGLRenderer;
  readonly camera: PerspectiveCamera;
  /** The main 3D world. */
  readonly scene = new Scene();
  /** Rendered on top with the depth buffer cleared, so the first-person hands
   *  never clip into geometry. */
  readonly hudScene = new Scene();

  constructor(container: HTMLElement) {
    this.renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.outputColorSpace = SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.camera = new PerspectiveCamera(
      CAMERA.BASE_FOV,
      window.innerWidth / window.innerHeight,
      CAMERA.NEAR,
      CAMERA.FAR,
    );

    window.addEventListener('resize', this.handleResize);
  }

  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  /** Draw the world, then overlay the hands scene on a cleared depth buffer. */
  render(): void {
    const r = this.renderer;
    r.autoClear = true;
    r.clear();
    r.render(this.scene, this.camera);
    r.autoClear = false;
    r.clearDepth();
    r.render(this.hudScene, this.camera);
  }

  private handleResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };
}
